// src/features/estoque/components/BarcodeScannerModal.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  extrairChaveAcesso,
  validarDigitoVerificadorNFe,
  playBeepSucesso,
  vibrarSucesso,
} from "utils/nfeUtils";
import { db } from "services/firebaseConfig";
import { useAuth } from "contexts/AuthContext";
import {
  doc,
  getDoc,
  collection,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import {
  Camera,
  Flashlight,
  Trash2,
  X,
  ScanLine,
  Layers,
  Send,
  Loader2,
  RefreshCw,
  Image,
  Keyboard,
  Check,
  RotateCw,
  Smartphone,
} from "lucide-react";
import toast from "react-hot-toast";

const SCANNER_CONTAINER_ID = "nfe-barcode-scanner-view";

const BarcodeScannerModal = ({ isOpen, onClose, onBatchSubmitted }) => {
  const { user } = useAuth();
  const [scannedKeys, setScannedKeys] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("environment");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== "undefined" ? window.innerWidth > window.innerHeight : false
  );

  // Controles Avançados de Foco e Zoom
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, available: false });
  const [currentZoom, setCurrentZoom] = useState(1);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualKeyValue, setManualKeyValue] = useState("");

  const html5QrCodeRef = useRef(null);
  const lastScannedKeyRef = useRef(null);
  const lastScanTimestampRef = useRef(0);
  const fileInputRef = useRef(null);
  const isStartingRef = useRef(false);

  // Monitorar rotação de tela
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("orientationchange", handleOrientationChange);
    return () => {
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  // Solicitar tela cheia e modo deitado quando o usuário clicar na dica
  const handleRequestLandscape = async () => {
    try {
      if (!document.fullscreenElement) {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      }
      if (window.screen?.orientation?.lock) {
        await window.screen.orientation.lock("landscape").catch(() => {});
      }
      toast.success("Vire o celular na horizontal!", { icon: "🔄", duration: 3000 });
    } catch (e) {
      toast("Vire o celular deitado (desative o bloqueio de rotação nas configurações do aparelho)", {
        icon: "📱",
        duration: 4000,
      });
    }
  };

  // Manipulador de leitura com debounce e validação
  const handleScanSuccess = useCallback(
    async (decodedText, decodedResult) => {
      const now = Date.now();
      if (now - lastScanTimestampRef.current < 1500) return;

      const chave = extrairChaveAcesso(decodedText);
      if (!chave) return;

      if (chave === lastScannedKeyRef.current && now - lastScanTimestampRef.current < 4000) {
        return;
      }

      lastScanTimestampRef.current = now;
      lastScannedKeyRef.current = chave;

      setScannedKeys((prev) => {
        if (prev.some((item) => item.chave === chave)) {
          toast("Esta nota já está no lote!", { icon: "⚠️" });
          return prev;
        }

        getDoc(doc(db, "historico_notas", chave))
          .then((docSnap) => {
            if (docSnap.exists()) {
              toast.error("Atenção: Esta nota já foi importada no estoque anteriormente!");
            }
          })
          .catch(() => {});

        playBeepSucesso();
        vibrarSucesso();
        toast.success(`Nota capturada! (...${chave.slice(-6)})`, {
          icon: "📦",
          duration: 2500,
        });

        return [
          {
            chave,
            horario: new Date().toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            }),
            formato: decodedResult?.result?.format?.formatName || "CODE_128",
          },
          ...prev,
        ];
      });
    },
    []
  );

  // Limpeza profunda de qualquer instância anterior do scanner e de tracks residuais
  const cleanExistingScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const instance = html5QrCodeRef.current;
        html5QrCodeRef.current = null;
        if (instance.isScanning) {
          await instance.stop();
        }
        instance.clear();
      } catch (e) {
        console.warn("Aviso ao limpar scanner:", e);
      }
    }
    const container = document.getElementById(SCANNER_CONTAINER_ID);
    if (container) {
      const videos = container.querySelectorAll("video");
      videos.forEach((video) => {
        if (video.srcObject && video.srcObject.getTracks) {
          video.srcObject.getTracks().forEach((track) => track.stop());
        }
        video.srcObject = null;
      });
      container.innerHTML = "";
    }
  };

  // Iniciar a câmera de forma robusta e livre de conflitos de estado
  const startCamera = useCallback(async () => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;

    try {
      setCameraError(null);
      await cleanExistingScanner();

      // Pequena pausa para garantir liberação do driver da câmera
      await new Promise((resolve) => setTimeout(resolve, 100));

      const scanner = new Html5Qrcode(SCANNER_CONTAINER_ID, {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.EAN_13,
        ],
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true,
        },
        verbose: false,
      });
      html5QrCodeRef.current = scanner;

      const scanConfig = {
        fps: 20,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const isLand = viewfinderWidth > viewfinderHeight;
          const width = isLand
            ? Math.min(Math.floor(viewfinderWidth * 0.90), 620)
            : Math.min(Math.floor(viewfinderWidth * 0.96), 460);
          const height = isLand
            ? Math.min(Math.floor(viewfinderHeight * 0.55), 180)
            : Math.min(Math.floor(viewfinderHeight * 0.45), 160);
          return { width, height };
        },
      };

      // PASSAGEM ESTRITAMENTE DE 1 CHAVE: { facingMode: cameraFacing }
      // html5-qrcode exige exatamente 1 chave quando passado objeto
      await scanner.start(
        { facingMode: cameraFacing },
        scanConfig,
        handleScanSuccess,
        () => {}
      );

      setIsScanning(true);

      // Inspeciona capacidades de hardware (Zoom e Lanterna)
      try {
        const trackCapabilities = scanner.getRunningTrackCapabilities();
        if (trackCapabilities) {
          if (trackCapabilities.torch) {
            setHasTorch(true);
          }

          if (trackCapabilities.zoom) {
            const minZ = trackCapabilities.zoom.min || 1;
            const maxZ = trackCapabilities.zoom.max || 4;
            setZoomRange({ min: minZ, max: maxZ, available: true });

            const initialZoom = Math.min(Math.max(1.8, minZ), maxZ);
            scanner
              .applyVideoConstraints({ advanced: [{ zoom: initialZoom }] })
              .then(() => setCurrentZoom(initialZoom))
              .catch(() => {});
          }
        }
      } catch (e) {
        console.log("Hardware zoom/torch não suportado:", e);
      }
    } catch (err) {
      console.error("Erro ao iniciar câmera:", err);
      const isPermissionDenied =
        err?.name === "NotAllowedError" ||
        err?.name === "PermissionDeniedError" ||
        String(err).includes("Permission") ||
        String(err).includes("NotAllowed");

      const errName = err?.name ? `[${err.name}] ` : "";

      setCameraError(
        isPermissionDenied
          ? "Permissão da câmera bloqueada. Toque no ícone de configurações/cadeado na barra do navegador e permita o uso da câmera."
          : `${errName}Não foi possível iniciar o vídeo da câmera (${err?.message || err}). Você pode tirar uma foto da nota com o botão abaixo.`
      );
      setIsScanning(false);
    } finally {
      isStartingRef.current = false;
    }
  }, [cameraFacing, handleScanSuccess]);

  // Parar a câmera
  const stopCamera = useCallback(async () => {
    await cleanExistingScanner();
    setIsScanning(false);
    setTorchOn(false);
  }, []);

  // Alternar Lanterna
  const toggleTorch = async () => {
    if (!html5QrCodeRef.current || !hasTorch) return;
    try {
      const nextState = !torchOn;
      await html5QrCodeRef.current.applyVideoConstraints({
        advanced: [{ torch: nextState }],
      });
      setTorchOn(nextState);
    } catch (e) {
      toast.error("Não foi possível alternar a lanterna.");
    }
  };

  // Ajustar Zoom Óptico/Digital do Aparelho
  const applyZoom = async (targetZoom) => {
    if (!html5QrCodeRef.current || !zoomRange.available) return;
    try {
      const clamped = Math.min(Math.max(targetZoom, zoomRange.min), zoomRange.max);
      await html5QrCodeRef.current.applyVideoConstraints({
        advanced: [{ zoom: clamped }],
      });
      setCurrentZoom(clamped);
    } catch (e) {
      console.error("Falha ao ajustar zoom:", e);
    }
  };

  // Alternar Câmera (traseira/frontal)
  const toggleCameraFacing = () => {
    setCameraFacing((prev) => (prev === "environment" ? "user" : "environment"));
  };

  // Forçar Foco Automático ao tocar na tela
  const handleTapToFocus = async () => {
    if (!html5QrCodeRef.current) return;
    try {
      await html5QrCodeRef.current.applyVideoConstraints({
        advanced: [{ focusMode: "continuous" }],
      });
      toast("Focando lente...", { icon: "🎯", duration: 1000 });
    } catch (e) {}
  };

  // Captura de Foto em Altíssima Resolução (Fallback Infalível quando o vídeo falha)
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !html5QrCodeRef.current) return;

    try {
      toast.loading("Processando foto em alta resolução...", { id: "photo-scan" });
      const decodedText = await html5QrCodeRef.current.scanFile(file, false);
      toast.dismiss("photo-scan");
      handleScanSuccess(decodedText, {
        result: { format: { formatName: "FOTO_ALTA_DEFINICAO" } },
      });
    } catch (err) {
      toast.error(
        "Não foi possível ler o código na foto. Certifique-se de que o código de barras está reto e focado.",
        { id: "photo-scan", duration: 4500 }
      );
    }
    e.target.value = null;
  };

  // Adicionar Chave Manualmente
  const handleAddManualKey = () => {
    const limpa = manualKeyValue.replace(/\D/g, "");
    if (limpa.length !== 44) {
      toast.error("A chave de acesso deve ter exatamente 44 dígitos numéricos.");
      return;
    }

    if (!validarDigitoVerificadorNFe(limpa)) {
      toast.error("Dígito verificador inválido. Verifique se digitou corretamente.");
      return;
    }

    handleScanSuccess(limpa, {
      result: { format: { formatName: "DIGITACAO_MANUAL" } },
    });
    setManualKeyValue("");
    setShowManualInput(false);
  };

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        startCamera();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      stopCamera();
    }
  }, [isOpen, startCamera, stopCamera]);

  const handleRemoveKey = (chaveToRemove) => {
    setScannedKeys((prev) => prev.filter((item) => item.chave !== chaveToRemove));
  };

  const handleSubmitBatch = async () => {
    if (scannedKeys.length === 0) return;

    setIsSubmitting(true);
    try {
      const batch = writeBatch(db);
      const queueCol = collection(db, "fila_importacao_nfe");

      scannedKeys.forEach((item) => {
        const docRef = doc(queueCol, item.chave);
        batch.set(
          docRef,
          {
            chave_acesso: item.chave,
            status: "pendente",
            criado_em: serverTimestamp(),
            criado_por: user?.displayName || user?.email || "Operador Mobile",
            origem: "camera_mobile",
            formato: item.formato,
          },
          { merge: true }
        );
      });

      await batch.commit();

      toast.success(
        `${scannedKeys.length} nota(s) enviada(s) para a fila com sucesso!`,
        { icon: "🚀", duration: 4000 }
      );

      setScannedKeys([]);
      if (onBatchSubmitted) onBatchSubmitted();
      handleClose();
    } catch (err) {
      console.error("Erro ao enviar lote de chaves:", err);
      toast.error(`Falha ao enviar: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    stopCamera();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className={`w-full h-[100dvh] max-h-none p-0 border-0 sm:border rounded-none sm:rounded-xl overflow-hidden flex bg-slate-950 text-white ${
          isLandscape
            ? "flex-row sm:max-w-4xl sm:h-[88vh]"
            : "flex-col sm:max-w-lg sm:h-auto sm:max-h-[92vh]"
        }`}
      >
        {/* Cabeçalho superior (Visível apenas em modo Retrato) */}
        {!isLandscape && (
          <DialogHeader className="px-4 py-3 bg-slate-900 border-b border-slate-800 flex flex-row items-center justify-between shrink-0">
            <DialogTitle className="text-base font-bold flex items-center gap-2 text-white">
              <Camera className="w-5 h-5 text-green-400" />
              Scanner de Notas Fiscais
            </DialogTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-green-500/20 text-green-300 font-semibold px-2.5 py-0.5 rounded-full border border-green-500/30">
                {scannedKeys.length} bipada(s)
              </span>
              <button
                onClick={handleClose}
                className="text-slate-400 hover:text-white p-1 rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </DialogHeader>
        )}

        {/* Área da Câmera (Comum aos dois modos - flex-1) */}
        <div
          key="camera-viewport-container"
          onClick={handleTapToFocus}
          className="relative flex-1 bg-black flex flex-col items-center justify-center overflow-hidden min-h-[260px] cursor-crosshair"
          title="Toque na tela para focar"
        >
          <div
            id={SCANNER_CONTAINER_ID}
            className="w-full h-full object-cover [&_video]:object-cover [&_video]:w-full [&_video]:h-full"
          />

          {/* Loader inicial enquanto a câmera acorda */}
          {!isScanning && !cameraError && (
            <div className="absolute inset-0 bg-slate-950 flex flex-col items-center justify-center gap-2 text-xs text-slate-400 z-10">
              <Loader2 className="w-7 h-7 animate-spin text-green-400" />
              <span>Iniciando câmera em alta resolução...</span>
            </div>
          )}

          {/* Mira decorativa sobreposta com aviso óptico e orientação */}
          <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center p-3">
            <div
              className={`border-2 border-dashed border-green-400/90 rounded-xl relative shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] flex items-center justify-center transition-all duration-300 ${
                isLandscape
                  ? "w-[90%] max-w-[620px] h-[120px]"
                  : "w-[95%] max-w-[420px] h-[125px]"
              }`}
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-900/95 text-green-400 text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-green-500/30 flex items-center gap-1 shadow-sm whitespace-nowrap">
                <ScanLine className="w-3.5 h-3.5 animate-pulse" />
                Enquadre o Código de Barras Inteiro
              </div>
              <div className="w-full h-[1px] bg-green-500/30 shadow-[0_0_8px_rgba(74,222,128,0.8)] animate-pulse" />
            </div>

            {/* Aviso e Dica Inteligente de Orientação */}
            {!isLandscape ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestLandscape();
                }}
                className="pointer-events-auto mt-3 flex items-center gap-1.5 bg-amber-500/25 hover:bg-amber-500/35 border border-amber-400/50 text-amber-200 text-[11px] px-3.5 py-1.5 rounded-full shadow-lg backdrop-blur-md transition-all active:scale-95"
                title="Clique para virar a tela"
              >
                <RotateCw className="w-3.5 h-3.5 text-amber-300 animate-spin-slow" />
                <span>
                  <strong>Vire o celular deitado:</strong> Leitura 10x mais rápida!
                </span>
              </button>
            ) : (
              <div className="mt-2 flex items-center gap-1.5 bg-emerald-500/25 border border-emerald-400/40 text-emerald-300 text-[11px] px-3 py-1 rounded-full shadow-lg backdrop-blur-md">
                <Smartphone className="w-3.5 h-3.5 rotate-90 text-emerald-400" />
                <span>Modo horizontal ativo • Resolução óptica máxima</span>
              </div>
            )}
          </div>

          {/* Seletor de Zoom Inteligente sobreposto */}
          {zoomRange.available && (
            <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-20 pointer-events-auto bg-black/60 backdrop-blur-md p-1 rounded-xl border border-white/10 shadow-lg">
              {[1, 1.5, 2, 2.5].map((level) => (
                <button
                  key={level}
                  onClick={(e) => {
                    e.stopPropagation();
                    applyZoom(level);
                  }}
                  className={`text-[11px] font-bold px-2 py-1 rounded-lg transition-colors ${
                    Math.abs(currentZoom - level) < 0.2
                      ? "bg-green-500 text-slate-950 shadow"
                      : "text-white/80 hover:bg-white/20"
                  }`}
                >
                  {level}x
                </button>
              ))}
            </div>
          )}

          {/* Botões de Ação na Câmera */}
          <div className="absolute bottom-3 left-0 right-0 flex justify-center items-center gap-2 px-3 z-20 pointer-events-auto flex-wrap">
            {hasTorch && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTorch();
                }}
                className={`bg-black/65 backdrop-blur-md border-slate-700 text-white hover:bg-black/80 rounded-full h-8 px-3 text-xs ${
                  torchOn ? "border-yellow-400 text-yellow-300 bg-yellow-950/40" : ""
                }`}
              >
                <Flashlight className="w-3.5 h-3.5 mr-1" />
                {torchOn ? "Lanterna Ligada" : "Lanterna"}
              </Button>
            )}

            {/* Botão de Tirar Foto em Alta Resolução */}
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              className="bg-black/65 backdrop-blur-md border-slate-700 text-white hover:bg-black/80 rounded-full h-8 px-3 text-xs"
              title="Tirar foto nítida com a câmera do celular"
            >
              <Image className="w-3.5 h-3.5 mr-1 text-blue-400" />
              Tirar Foto HD
            </Button>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={fileInputRef}
              onChange={handlePhotoUpload}
              className="hidden"
            />

            {/* Digitar Manualmente */}
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowManualInput(!showManualInput);
              }}
              className="bg-black/65 backdrop-blur-md border-slate-700 text-white hover:bg-black/80 rounded-full h-8 px-3 text-xs"
            >
              <Keyboard className="w-3.5 h-3.5 mr-1 text-amber-400" />
              Digitar
            </Button>

            {/* Botão Virar Tela se estiver em retrato */}
            {!isLandscape && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestLandscape();
                }}
                className="bg-black/65 backdrop-blur-md border-slate-700 text-amber-300 hover:bg-black/80 rounded-full h-8 px-2.5 text-xs"
                title="Girar para horizontal"
              >
                <RotateCw className="w-3.5 h-3.5 mr-1" />
                Girar
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                toggleCameraFacing();
              }}
              className="bg-black/65 backdrop-blur-md border-slate-700 text-white hover:bg-black/80 rounded-full h-8 px-2.5 text-xs"
              title="Inverter Câmera"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* Mensagem de Erro de Câmera */}
          {cameraError && (
            <div className="absolute inset-0 bg-slate-950/95 p-5 flex flex-col items-center justify-center text-center z-30 overflow-y-auto">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center mb-3">
                <Camera className="w-6 h-6" />
              </div>

              <h4 className="text-white font-bold text-sm mb-1">
                Acesso ao Vídeo Indisponível
              </h4>

              <p className="text-slate-300 text-xs mb-4 max-w-xs leading-relaxed">
                {cameraError}
              </p>

              {/* Ações imediatas para o usuário nunca ficar travado */}
              <div className="flex flex-col gap-2 w-full max-w-xs">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs h-10 w-full shadow-lg shadow-blue-900/30 flex items-center justify-center gap-2"
                >
                  <Image className="w-4 h-4 text-blue-200" />
                  Tirar Foto HD da Nota (Câmera do Celular)
                </Button>

                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowManualInput(true);
                  }}
                  variant="outline"
                  className="border-slate-700 bg-slate-900 text-white hover:bg-slate-800 text-xs h-9 w-full flex items-center justify-center gap-2"
                >
                  <Keyboard className="w-4 h-4 text-amber-400" />
                  Digitar Chave de 44 Dígitos
                </Button>

                <div className="flex gap-2 mt-1">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      startCamera();
                    }}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-slate-700 text-slate-300 hover:text-white text-xs h-8"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                    Tentar Novamente
                  </Button>
                  <Button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        if ("caches" in window) {
                          const keys = await caches.keys();
                          await Promise.all(keys.map((k) => caches.delete(k)));
                        }
                        if ("serviceWorker" in navigator) {
                          const regs = await navigator.serviceWorker.getRegistrations();
                          await Promise.all(regs.map((r) => r.unregister()));
                        }
                      } catch (err) {}
                      window.location.reload(true);
                    }}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-amber-500/40 text-amber-300 hover:bg-amber-500/20 text-xs h-8"
                  >
                    Limpar Cache
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Painel do Lote / Gaveta (Horizontal: Barra Lateral; Vertical: Gaveta Inferior) */}
        <div
          className={`bg-slate-900 flex flex-col shrink-0 ${
            isLandscape
              ? "w-72 sm:w-80 h-full border-l border-slate-800"
              : "border-t border-slate-800 max-h-[220px]"
          }`}
        >
          {/* Cabeçalho do painel lateral (apenas em modo Paisagem) */}
          {isLandscape && (
            <div className="px-4 py-3 bg-slate-900 border-b border-slate-800 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-green-400" />
                <span className="font-bold text-sm text-white">Scanner NFe</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs bg-green-500/20 text-green-300 font-semibold px-2 py-0.5 rounded-full border border-green-500/30">
                  {scannedKeys.length} bip(s)
                </span>
                <button
                  onClick={handleClose}
                  className="text-slate-400 hover:text-white p-1 rounded-md transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Caixa Retrátil de Digitação Manual */}
          {showManualInput && (
            <div className="bg-slate-950/80 border-b border-slate-800 p-3 flex flex-col gap-2 shrink-0 animate-in fade-in slide-in-from-top-2">
              <div className="flex justify-between items-center text-xs">
                <span className="font-semibold text-slate-300">
                  Chave (44 dígitos):
                </span>
                <span className="text-slate-400 font-mono">
                  {manualKeyValue.replace(/\D/g, "").length} / 44
                </span>
              </div>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Ex: 3524 0908 8556..."
                  value={manualKeyValue}
                  onChange={(e) => setManualKeyValue(e.target.value)}
                  maxLength={54}
                  className="font-mono text-xs bg-slate-900 border-slate-700 text-white h-8"
                />
                <Button
                  onClick={handleAddManualKey}
                  disabled={manualKeyValue.replace(/\D/g, "").length !== 44}
                  className="bg-green-600 hover:bg-green-500 text-white h-8 px-2.5 shrink-0"
                >
                  <Check className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Lista de Notas */}
          <div className="p-3 flex-1 flex flex-col min-h-0">
            <div className="flex justify-between items-center mb-2 shrink-0">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-slate-400" />
                Lote ({scannedKeys.length})
              </span>
              {scannedKeys.length > 0 && (
                <button
                  onClick={() => setScannedKeys([])}
                  className="text-[11px] text-red-400 hover:text-red-300 font-medium"
                >
                  Limpar
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 min-h-[60px]">
              {scannedKeys.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-3 text-slate-500 text-xs">
                  <span>Nenhuma nota lida.</span>
                  <span className="text-[11px] text-slate-600 mt-0.5">
                    {isLandscape
                      ? "Aponte a mira para o código de barras."
                      : "Vire na horizontal para melhor foco."}
                  </span>
                </div>
              ) : (
                scannedKeys.map((item) => (
                  <div
                    key={item.chave}
                    className="bg-slate-800/80 border border-slate-700/80 rounded-lg p-2 flex justify-between items-center text-xs"
                  >
                    <div className="flex flex-col truncate pr-2">
                      <span className="font-mono text-[11px] text-green-300 font-bold tracking-wider truncate">
                        {item.chave.replace(/(.{4})/g, "$1 ").trim()}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {item.horario} • {item.formato}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveKey(item.chave)}
                      className="text-slate-400 hover:text-red-400 p-1 rounded shrink-0"
                      title="Remover do lote"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Botão de Confirmação e Envio */}
            <div className="mt-2 pt-2 border-t border-slate-800 flex gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1 border-slate-700 bg-slate-800 text-white hover:bg-slate-700 text-xs h-9"
              >
                Fechar
              </Button>
              <Button
                onClick={handleSubmitBatch}
                disabled={scannedKeys.length === 0 || isSubmitting}
                className="flex-1 bg-green-600 hover:bg-green-500 text-white font-bold text-xs h-9 shadow-lg shadow-green-900/30"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    Enviar ({scannedKeys.length})
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScannerModal;
